import { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { mediaUrl } from "@/lib/api";

function fmt(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function AudioPlayer({ path, title }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => setDur(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const seek = (e) => {
    const a = ref.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * dur;
  };

  return (
    <div className="flex items-center gap-4 bg-[#F3E8E2] border border-[rgba(61,50,56,0.08)] rounded-full px-4 py-3" data-testid="audio-player">
      <audio ref={ref} src={mediaUrl(path)} preload="metadata" />
      <button onClick={toggle} data-testid="audio-play-toggle"
        className="w-11 h-11 shrink-0 rounded-full bg-[#C6899E] text-[#4A2233] flex items-center justify-center hover:bg-[#B4718C] transition-colors">
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        {title && <div className="text-sm text-[#3D3238] truncate mb-1.5">{title}</div>}
        <div className="h-1 bg-[#E9DBD3] rounded-full cursor-pointer" onClick={seek}>
          <div className="h-full bg-[#C6899E] rounded-full" style={{ width: `${dur ? (cur / dur) * 100 : 0}%` }} />
        </div>
      </div>
      <span className="text-xs text-[#9C8F94] tabular-nums shrink-0">{fmt(cur)} / {fmt(dur)}</span>
    </div>
  );
}
